/**
 * The OAuth 2.0 authorization-code exchange, spoken directly to Cognito.
 *
 * No SDK. Amplify would pull a large dependency tree to wrap four URL builders
 * and one form POST, and it stores tokens in localStorage by default — the one
 * decision this design most wants to make differently (see `session.ts`).
 */
import type { AuthConfig } from './config.ts';
import { createCodeVerifier, createStateToken, deriveCodeChallenge } from './pkce.ts';
import { setPending, type Tokens } from './session.ts';

export class AuthorizationError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AuthorizationError';
  }
}

interface TokenResponse {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

function tokenEndpoint(config: AuthConfig): string {
  return `${config.domain}/oauth2/token`;
}

async function exchange(config: AuthConfig, body: URLSearchParams): Promise<Tokens> {
  let response: Response;
  try {
    response = await fetch(tokenEndpoint(config), {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    });
  } catch {
    throw new AuthorizationError(
      'identity_provider_unreachable',
      'Could not reach the sign-in service. Check your connection and try again.',
    );
  }

  const payload = (await response.json().catch(() => ({}))) as TokenResponse;

  if (!response.ok || payload.error !== undefined) {
    // Cognito's error_description is written for a developer, not an operator,
    // and occasionally echoes request detail. The code is kept for the UI to
    // branch on; the prose shown to the user is ours.
    throw new AuthorizationError(
      payload.error ?? 'token_exchange_failed',
      'Sign-in did not complete.',
    );
  }

  if (typeof payload.id_token !== 'string' || typeof payload.access_token !== 'string') {
    throw new AuthorizationError('token_exchange_failed', 'Sign-in did not complete.');
  }

  return {
    idToken: payload.id_token,
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token ?? null,
    expiresAt: Date.now() + (payload.expires_in ?? 3600) * 1000,
  };
}

/**
 * Sends the browser to Cognito's hosted sign-in.
 *
 * `prompt=none` asks for a silent answer: if Cognito's session cookie is still
 * valid the user is bounced straight back with a code and never sees a form,
 * which is what makes a page reload feel like nothing happened.
 *
 * Cognito's hosted UI does NOT honour it when the session has expired — observed
 * in production, it renders its sign-in form rather than returning
 * `error=login_required` as OIDC specifies. The parameter is still sent because
 * it costs nothing and is correct; the callback handles `login_required` for a
 * provider that follows the spec (ADR-013).
 */
export async function beginAuthorization(
  config: AuthConfig,
  options: { returnTo: string; silent: boolean },
): Promise<void> {
  const verifier = createCodeVerifier();
  const state = createStateToken();
  setPending({ verifier, state, returnTo: options.returnTo });

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: 'openid email profile',
    state,
    code_challenge: await deriveCodeChallenge(verifier),
    code_challenge_method: 'S256',
  });
  if (options.silent) params.set('prompt', 'none');

  window.location.assign(`${config.domain}/oauth2/authorize?${params.toString()}`);
}

export function completeAuthorization(
  config: AuthConfig,
  code: string,
  verifier: string,
): Promise<Tokens> {
  return exchange(
    config,
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }),
  );
}

export async function refreshTokens(config: AuthConfig, refreshToken: string): Promise<Tokens> {
  const refreshed = await exchange(
    config,
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    }),
  );
  // A refresh response omits the refresh token; carrying the old one forward is
  // what keeps the session alive past the first renewal.
  return { ...refreshed, refreshToken: refreshed.refreshToken ?? refreshToken };
}

/**
 * Ends the Cognito session, not just this tab's.
 *
 * Dropping the local tokens alone would leave the user signed in as far as
 * Cognito is concerned, so the next sign-in would skip the password prompt
 * entirely — which looks exactly like sign-out having failed, and on a shared
 * machine, is.
 */
export function endSession(config: AuthConfig): void {
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutUri,
  });
  window.location.assign(`${config.domain}/logout?${params.toString()}`);
}

/** Claims the console reads from the id token. Never trusted for authorization. */
export interface IdTokenClaims {
  readonly sub: string;
  readonly email: string;
  readonly name?: string;
}

/**
 * Reads the id token's payload for display purposes only.
 *
 * Deliberately does NOT verify the signature. A browser-side check proves
 * nothing an attacker who controls the browser cannot fake, and treating it as
 * proof invites exactly the mistake of authorizing from claims. Every
 * authorization decision is made by the API, which verifies the token properly
 * against the JWKS on every request. This is here so the user menu can show a
 * name before /v1/me answers.
 */
export function readIdTokenClaims(idToken: string): IdTokenClaims | null {
  const payload = idToken.split('.')[1];
  if (payload === undefined) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as Record<string, unknown>;
    if (typeof claims['sub'] !== 'string' || typeof claims['email'] !== 'string') return null;
    return {
      sub: claims['sub'],
      email: claims['email'],
      ...(typeof claims['name'] === 'string' ? { name: claims['name'] } : {}),
    };
  } catch {
    return null;
  }
}
